<template>
	<div class="Map">
		<div class="Map-map" ref="map"></div>
		<template v-if="Boolean(this.google) && Boolean(this.map)">
			<slot :google="google" :map="map" />
		</template>
	</div>
</template>

<script>
export default {
	name: 'CloMapGoogleLoader',
	props: {
		apiKey: {
			type: String,
			default: '',
		},
		googleMapsLibraryUrl: {
			type: String,
			default: null,
		},
		lazy: {
			type: Boolean,
			default: true,
		},
		markers: {
			type: Array,
			default() {
				return [];
			},
		},
		bounds: {
			type: Object,
			default: null,
		},
		mapTypeId: {
			type: String,
			default: 'satellite',
		},
		streetViewControl: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			google: null,
			map: null,
			mapWidth: 0,
		};
	},
	computed: {
		url() {
			if (this.googleMapsLibraryUrl) {
				return this.googleMapsLibraryUrl + '&callback=googleMapsApiLoaded';
			}
			return 'https://maps.googleapis.com/maps/api/js?key=' + this.apiKey + '&libraries=places&callback=googleMapsApiLoaded';
		},
	},
	watch: {
		bounds: {
			deep: true,
			handler() {
				if (this.bounds) {
					this.changeBounds();
				}
			},
		},
		mapTypeId() {
			this.map.setMapTypeId(this.mapTypeId);
		},
	},
	mounted() {
		if (this.lazy) {
			const io = new IntersectionObserver((changes) => {
				changes.forEach((change) => {
					if (change.intersectionRatio > 0) {
						this.loadLibraryIfNeeded();
						io.unobserve(this.$el);
					}
				});
			});
			io.observe(this.$el);
		} else {
			this.loadLibraryIfNeeded();
		}
	},
	beforeDestroy() {
		window.document.removeEventListener('lichen:googleMapsApiLoaded', this.initializeMap);
		this.google.maps.event.clearListeners(this.map, 'zoom_changed');
		this.google.maps.event.clearListeners(this.map, 'dragend');
		window.document.removeEventListener('lichen:resize', this.handleResize);
	},
	methods: {
		loadLibraryIfNeeded() {
			if (typeof google == 'undefined') {
				this.loadGoogleMapsLibrary();
				window.document.addEventListener('lichen:googleMapsApiLoaded', this.initializeMap);
			} else {
				this.initializeMap();
			}
		},
		initializeMap() {
			this.google = google;
			this.map = new google.maps.Map(this.$refs.map, { 
				mapTypeId: this.mapTypeId,
				streetViewControl: this.streetViewControl,
			});

			//setting bounds for markers
			if (this.markers.length) {
				const bounds = new this.google.maps.LatLngBounds();
				this.markers.forEach((marker) => {
					bounds.extend(marker.position);
				});

				// seems like we can't avoid this for single loc?
				this.map.fitBounds(bounds);
				// set max zoom for single loc
				if (this.markers.length == 1) {
					let latLng = new this.google.maps.LatLng(this.markers[0].position.lat, this.markers[0].position.lng);
					this.setMaxZoomForSingleLoc(latLng);
				}
			} else if (!this.bounds) {
				const bounds = new this.google.maps.LatLngBounds(
					new this.google.maps.LatLng(85, -180), // top left corner of map
					new this.google.maps.LatLng(-85, 180) // bottom right corner
				);
				this.map.fitBounds(bounds);
			}

			this.google.maps.event.addListener(this.map, 'zoom_changed', this.zoomChanged);
			this.google.maps.event.addListener(this.map, 'dragend', this.dragEnd);
			this.mapWidth = this.$el.clientWidth;
			window.document.addEventListener('lichen:resize', this.handleResize);
		},
		handleResize() {
			const newMapWidth = this.$el.clientWidth;
			if (newMapWidth !== this.mapWidth) {
				this.mapWidth = newMapWidth;
				const center = this.map.getCenter();
				this.google.maps.event.trigger(this.map, 'resize');
				this.map.setCenter(center);
			}
		},
		changeBounds() {
			console.log(JSON.parse(JSON.stringify(this.bounds)));
			if (this.bounds.hasOwnProperty('maxX')) {
				let newBounds = this.bounds;
				// better bounds for USA
				if (newBounds.maxX === 179.773408 && newBounds.maxY === 71.390685) {
					newBounds.maxX = -65;
					newBounds.maxY = 67;
				}
				// better bounds for Russia
				if (newBounds.minX === -180.000001 && newBounds.maxY === 81.857324) {
					newBounds.minX = 35;
					newBounds.maxY = 74;
				}
				const bounds = new google.maps.LatLngBounds(
					new google.maps.LatLng(this.bounds.minY, this.bounds.minX),
					new google.maps.LatLng(this.bounds.maxY, this.bounds.maxX)
				);
				this.map.fitBounds(bounds);
			} else {
				this.map.fitBounds(this.bounds);
			}
		},
		zoomChanged() {
			this.$emit('zoom_changed', this.getZoomAndBounds());
		},
		dragEnd() {
			this.$emit('dragend', this.getZoomAndBounds());
		},
		getZoomAndBounds() {
			const bounds = this.map.getBounds();
			const ne = bounds.getNorthEast();
			const sw = bounds.getSouthWest();
			return {
				bounds: {
					minY: sw.lat(),
					minX: sw.lng(),
					maxY: ne.lat(),
					maxX: ne.lng(),
				},
				zoom: this.map.getZoom(),
			};
		},
		loadGoogleMapsLibrary() {
			if (!window.googleMapsApiLoaded) {
				window.googleMapsApiLoaded = () => {
					if (typeof google == 'undefined') {
						//this.googleMapsApiLoaded;
						const script_tag = window.document.createElement('script');
						script_tag.setAttribute('type', 'text/javascript');
						script_tag.setAttribute('src', this.url + '&loading=async');
						(window.document.getElementsByTagName('head')[0] || window.document.documentElement).appendChild(script_tag);
					} else {
						//this.googleMapsApiLoaded;
						window.document.dispatchEvent(new CustomEvent('lichen:googleMapsApiLoaded'));
					}
				};
				window.googleMapsApiLoaded();
			} else {
				//this.googleMapsApiLoaded = true;
				//this.initializeMap();
			}
		},
		setMaxZoomForSingleLoc(latLng) {
			let maxZoomService = new google.maps.MaxZoomService();
			maxZoomService.getMaxZoomAtLatLng(latLng, (result) => {
				if (result.status !== 'OK') {
					console.log('MaxZoomService error');
					// TODO shall we default to 10 on error?
					this.map.setCenter(this.bounds.getCenter());
					this.map.setZoom(10);
				} else {
					this.map.setZoom(result.zoom);
				}
			});
		},
	},
};
</script>
<style lang="scss">
.Map {
	position: relative;
	overflow: hidden;
	&:before {
		display: block;
		content: '';
		width: 100%;
		padding-top: 50%;
	}
	.Map-map {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}
	// apply typography to info windows
	.gm-style-iw {
		font-family: $default-font-family;
		@include text(2);
		color: $color-text;
	}
}
</style>
